import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';

const LandingPageNavbar: React.FC = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  // Get correct dashboard route based on user role
  const getDashboardRoute = () => {
    if (!userProfile) return '/brand-dashboard'; // Fallback
    switch (userProfile.role) {
      case 'brand':
        return '/brand-dashboard';
      case 'creator':
        return '/creator-dashboard';
      case 'admin':
        return '/brand-dashboard';
      default:
        return '/brand-dashboard';
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-black/95 backdrop-blur-lg border-b border-white/10 shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div
            className="flex items-center gap-3 cursor-pointer group"
            onClick={() => navigate('/')}
          >
            <img
              src="/assets/images/Noonx_logo_small.png"
              alt="NoonX Platform"
              className="h-10 w-auto object-contain group-hover:scale-105 transition-transform duration-300"
              loading="eager"
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <button
              onClick={() => scrollToSection('campaigns')}
              className="text-gray-300 hover:text-white transition-colors duration-300 text-sm font-medium"
            >
              Campaigns
            </button>
            <button
              onClick={() => scrollToSection('features')}
              className="text-gray-300 hover:text-white transition-colors duration-300 text-sm font-medium"
            >
              Features
            </button>
            <button
              onClick={() => scrollToSection('testimonials')}
              className="text-gray-300 hover:text-white transition-colors duration-300 text-sm font-medium"
            >
              Testimonials
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="text-gray-300 hover:text-white transition-colors duration-300 text-sm font-medium"
            >
              About
            </button>
          </div>

          {/* Desktop CTA Buttons */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <Button
                onClick={() => navigate(getDashboardRoute())}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-2.5 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Go to Dashboard
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => navigate('/authentication')}
                  variant="ghost"
                  className="text-gray-300 hover:text-white transition-colors duration-300 font-medium"
                >
                  Sign In
                </Button>
                <Button
                  onClick={() => navigate('/authentication')}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-2.5 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Get Started
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-white hover:text-purple-400 transition-colors duration-300"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-black/98 backdrop-blur-lg border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
            <button
              onClick={() => scrollToSection('campaigns')}
              className="block w-full text-left text-gray-300 hover:text-white transition-colors duration-300 py-3 text-base font-medium border-b border-white/5"
            >
              Campaigns
            </button>
            <button
              onClick={() => scrollToSection('features')}
              className="block w-full text-left text-gray-300 hover:text-white transition-colors duration-300 py-3 text-base font-medium border-b border-white/5"
            >
              Features
            </button>
            <button
              onClick={() => scrollToSection('testimonials')}
              className="block w-full text-left text-gray-300 hover:text-white transition-colors duration-300 py-3 text-base font-medium border-b border-white/5"
            >
              Testimonials
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="block w-full text-left text-gray-300 hover:text-white transition-colors duration-300 py-3 text-base font-medium border-b border-white/5"
            >
              About
            </button>

            <div className="pt-4 space-y-3">
              {user ? (
                <Button
                  onClick={() => {
                    navigate(getDashboardRoute());
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-3 rounded-full font-semibold"
                >
                  Go to Dashboard
                </Button>
              ) : (
                <>
                  <Button
                    onClick={() => {
                      navigate('/authentication');
                      setIsMobileMenuOpen(false);
                    }}
                    variant="outline"
                    className="w-full border-white/20 text-white hover:bg-white/10 py-3 rounded-full font-medium"
                  >
                    Sign In
                  </Button>
                  <Button
                    onClick={() => {
                      navigate('/authentication');
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-3 rounded-full font-semibold"
                  >
                    Get Started
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default LandingPageNavbar;
